// @flow

import * as React from 'react';

import { useDispatch } from 'react-redux';

import {
  updateCartProduct,
  updateProductAndGetCartOrNotify,
  selectOneProductErrors,
  webhosting,
  type DecoratedProduct,
} from '@gandi/cart';
import { useTranslator } from '@gandi/translate';

import { useAppSelector } from 'src/hooks';

import { selectOrdersListUrl } from '../../../../SimpleHosting/modules/urls';

import ProductItem from './components/ProductItem';
import { DurationRow, OperationAlreadyExistsErrorRow } from './components/Rows';
import type { GandiFormSelectOption } from './types';

export type Props = {
  product: DecoratedProduct,
};

export function PaasRenew({ product }: Props): React.MixedElement {
  const { __, formatDate } = useTranslator();
  const dispatch = useDispatch();

  const productErrors = useAppSelector(state =>
    selectOneProductErrors(state, {
      productId: product.id,
    }),
  );

  // migrate legacy payload to a new one
  // it is necessary a legacy shs
  // TODO: this code will be obsolete begin of 2025
  // as the same time we could remove the route src/SimpleHosting/Renew
  React.useEffect(
    () => {
      if (product.payload.shopping === undefined) {
        dispatch(
          updateCartProduct({
            productId: product.id,
            payload: {
              duration: product.payload.duration,
              duration_unit: 'm',
              shopping: {
                durations: [1, 3, 6, 12],
                label: product.payload.label,
              },
            },
          }),
        );
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [] /* didMount like */,
  );

  const { dateStart, dateEnd } = webhosting.getRenewalPeriod(product);

  const ordersListUrl = useAppSelector(selectOrdersListUrl);

  const handleDurationChange: (
    selectedOption: GandiFormSelectOption<string>,
  ) => void = selectedOption => {
    const duration = parseInt(selectedOption.value, 10);
    const newPayload = { ...product.payload, duration };

    dispatch(updateProductAndGetCartOrNotify(__, product.id, newPayload));
  };

  const label = __('Web Hosting');

  const detail: React.Node =
    dateStart && dateEnd
      ? __(
          'Your Web Hosting %(label)s will be renewed for the period from %(dateStart)s to %(dateEnd)s.',
          {
            label,
            dateStart: formatDate(dateStart, 'll'),
            dateEnd: formatDate(dateEnd, 'll'),
          },
        )
      : undefined;
  const info: ?string =
    dateEnd &&
    (product.payload.shopping?.autorenew ||
      product.payload.shopping?.subscription)
      ? __(
          'From %(dateEnd)s, subject to suficient funds, it will be renewed automatically.',
          { dateEnd: formatDate(dateEnd, 'll') },
        )
      : __(
          'Then, you will need to renew your Web Hosting manually or activate automatic renewal.',
        );

  const hasOperationAlreadyExistsError =
    productErrors &&
    productErrors.some(
      error => error.location === 'path' && error.name === 'instance',
    );

  const price = product.isWorking ? undefined : product.total?.before_taxes;

  const footer = product.payload.shopping?.durations && (
    <DurationRow
      productId={product.id}
      /* $FlowFixMe[incompatible-type] unit / duration_unit are described differently between the payload and the component */
      unit={product.payload.duration_unit}
      value={product.payload.duration || 1}
      values={product.payload.shopping.durations}
      // $FlowFixMe[incompatible-type-arg] - number is number|string
      onChange={handleDurationChange}
      disabledOption={!!product.isWorking}
      total={price}
    />
  );

  return (
    <ProductItem
      key={`${product.id}-row`}
      productId={product.id}
      label={label}
      detail={detail}
      info={info}
      hasTaxes={footer ? undefined : product.hasTaxes}
      priceBeforeTaxes={footer ? undefined : price}
      footer={footer}
      errors={
        hasOperationAlreadyExistsError ? (
          <OperationAlreadyExistsErrorRow ordersListUrl={ordersListUrl} />
        ) : (
          productErrors
        )
      }
    />
  );
}
